import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@parrot-software/design-system';

export interface SubscriptionBreadcrumbItemType {
  label: string;
  path?: string;
}

interface SubscriptionBreadcrumbProps {
  isRootLinked?: boolean;
  items: SubscriptionBreadcrumbItemType[];
  rootPath?: string;
}

/**
 * SubscriptionBreadcrumb - Reusable breadcrumb navigation for subscription pages
 *
 * Automatically prepends "Suscripción" as the first item linking to the subscriptions root.
 * Items with a `path` are clickable links; items without a `path` are displayed as the current page.
 * With `isRootLinked` false the first item loses its link, for organizations whose list would only
 * bounce back to this same page.
 *
 * @param {SubscriptionBreadcrumbProps} props - Component properties
 * @param {SubscriptionBreadcrumbItemType[]} props.items - Array of breadcrumb items after "Suscripción"
 * @param {string} [props.rootPath='/subscriptions'] - Path to subscriptions root
 * @returns {React.ReactElement} Breadcrumb component
 *
 * @example
 * // Simple usage — block detail page
 * <SubscriptionBreadcrumb items={[{ label: 'Región Centro' }]} />
 *
 * @example
 * // Multiple segments
 * <SubscriptionBreadcrumb items={[
 *   { label: 'Región Centro', path: '/subscriptions/blocks/abc' },
 *   { label: 'Editar' }
 * ]} />
 */
export const SubscriptionBreadcrumb = ({
  isRootLinked = true,
  items,
  rootPath = '/subscriptions',
}: SubscriptionBreadcrumbProps) => {
  const { t } = useTranslation('subscriptions');

  const breadcrumbItems: SubscriptionBreadcrumbItemType[] = [
    { label: t('page.title'), path: isRootLinked ? rootPath : undefined },
    ...items,
  ];

  return (
    <Breadcrumb>
      <BreadcrumbList>
        {breadcrumbItems.flatMap((item, index) => {
          const isLast = index === breadcrumbItems.length - 1;
          const itemKey = item.path ?? item.label;

          return [
            <BreadcrumbItem key={`item-${itemKey}`}>
              {item.path && !isLast ? (
                <BreadcrumbLink asChild>
                  <Link to={item.path}>{item.label}</Link>
                </BreadcrumbLink>
              ) : (
                <BreadcrumbPage>{item.label}</BreadcrumbPage>
              )}
            </BreadcrumbItem>,
            ...(isLast ? [] : [<BreadcrumbSeparator key={`sep-${itemKey}`} />]),
          ];
        })}
      </BreadcrumbList>
    </Breadcrumb>
  );
};
